// confirm.ts — THE confirm-destructive primitive (Styles x Themes, PR-3b).
//
// "How does this product ask before it destroys something?" now has exactly
// ONE answer, and it lives in one file.
//
// The WF-A audit measured FOUR answers coexisting: a bare window.confirm, an
// ad-hoc `.armed` second click on `.ctrl.danger`, a dot-pivot pill, and — for
// seven destructive actions — nothing at all. PR-2 (#470) unified the
// INTERACTION into a capture-phase arm-then-fire and deleted the other three.
// It left the HOSTING split: the console's `confirmDialog` rendered a real
// dialog through the console engine, the arm rendered on the button, and the
// two knew nothing about each other. PR-3b closes that: both tiers live here,
// over the ONE modal host, and primitives-check refuses a destructive
// affordance that pairs with neither.
//
// ── the two tiers, and the rule for picking one ───────────────────────────
//
//   INLINE  (installConfirmDestructive + `data-btn-confirm`)
//           The default. The ask happens ON the control: first click ARMS
//           (glyph -> check, tooltip and aria-label -> the question, ramp goes
//           hot), second click fires; pointer leave, blur, Escape, an outside
//           click or 4s of silence disarms. Nothing is hosted, nothing is
//           mounted, and the gesture costs one extra click. Right whenever the
//           consequence fits in the button's own name ("Delete profile?").
//
//   HOSTED  (confirmModal)
//           When the consequence needs a SENTENCE — what else stops, what
//           cannot be undone, which named thing is affected. The layout spec
//           below is the console's, ported verbatim, and it exists because
//           porting the old bare `<p>` + two identical buttons FAILED the seam:
//           a confirmation that does not name the consequence is decoration.
//
// A destructive-intent control MUST pair with one of the two, or carry a NAMED
// `noConfirm:` reason (rendered into the DOM as `data-btn-noconfirm`, so the
// exemption is visible in the page and not only in source). That pairing is a
// primitives-check row: `destructive without a confirm pairing: 0`.
//
// ── why the inline tier is a capture-phase listener ───────────────────────
// It is installed ONCE per document, in capture phase, so it lands ahead of
// every delegated handler in every view and NO call site has to know it
// exists — the existing click handler simply never sees the first click. That
// is what let seven ungated destructive actions gain a gate without seven
// edits, and it is why a view that mounts standalone (a probe page) must
// install it too or the gesture under test does not exist.

import {
  openModal, type ModalHandle, type ModalVocabulary, HC_VOCAB,
} from './modal-host.js';
import { mkIconBtn, setBtnState, setBtnGlyph, setBtnLabel } from './buttons.js';

// ------------------------------------------------------------ hosted tier

export interface ConfirmSpec {
  /** Title line naming the decision ("Abort release 'X'?"). */
  title: string;
  /** Body copy naming the consequence. Rendered as textContent, pre-line. */
  body: string;
  /** The verb on the primary action (default 'Confirm'). */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive: the PRIMARY action carries the destructive intent ramp. */
  danger?: boolean;
  /** Fired on confirm. The dialog leaves NOW; this work rides behind it. */
  onConfirm: () => void;
  onClose?: () => void;
}

export interface ConfirmButtonVm {
  act: 'no' | 'yes';
  label: string;
  classes: string[];
}

/** The confirmation layout as DATA: DOM order IS visual order (actions are
 * right-aligned, so last-in-DOM = rightmost = primary). Cancel first, the
 * verb-labelled primary last; danger lands ONLY on the primary. */
export function confirmVm(spec: ConfirmSpec): {
  title: string; body: string; buttons: ConfirmButtonVm[];
} {
  return {
    title: spec.title,
    body: spec.body,
    buttons: [
      { act: 'no', label: spec.cancelLabel ?? 'Cancel', classes: ['hc-btn'] },
      {
        act: 'yes',
        label: spec.confirmLabel ?? 'Confirm',
        classes: spec.danger === true
          ? ['hc-btn', 'hc-btn-primary', 'hc-btn-primary-danger']
          : ['hc-btn', 'hc-btn-primary'],
      },
    ],
  };
}

/**
 * THE HOSTED TIER. A confirmation dialog on the one modal host.
 *
 * Confirm LEAVES NOW: the primary goes busy, the exit starts, and onConfirm
 * runs behind the close. An awaited POST under an open dialog is the REL-089
 * five-re-click incident; never reintroduce it. Escape/scrim/Cancel dismiss
 * with 0 side effects.
 */
export function confirmModal(
  host: HTMLElement,
  spec: ConfirmSpec,
  vocab: ModalVocabulary = HC_VOCAB,
): ModalHandle {
  const vm = confirmVm(spec);
  return openModal(host, (body, close) => {
    const title = document.createElement('h2');
    title.className = vocab.title;
    title.textContent = vm.title;
    body.appendChild(title);
    const copy = document.createElement('p');
    copy.className = vocab.body;
    copy.textContent = vm.body;
    body.appendChild(copy);
    const actions = document.createElement('div');
    actions.className = vocab.actions;
    for (const b of vm.buttons) {
      // Both buttons are ICON-ONLY .ibtn — the verb that used to be the
      // button's text is the whole tooltip and accessible name, and the INTENT
      // ramp paints what hc-btn-primary / hc-btn-primary-danger used to.
      // vm.classes is left standing as the layout SPEC's record of which
      // button is primary/danger; it is deliberately not applied to the DOM.
      const btn = b.act === 'yes'
        ? mkIconBtn({
            glyph: 'check',
            label: b.label,
            intent: spec.danger === true ? 'destructive' : 'additive',
            // Per DIALOG, not per surface: two stacked confirmations never
            // share a view context, so the one-additive-per-context rule
            // stays true however many dialogs the product grows.
            ctx: `confirm-dialog:${spec.title}`,
            // The INLINE tier is deliberately OFF here: this IS the confirm,
            // and arming a second question inside it would be the
            // double-confirm the one-primitive ruling exists to kill.
            noConfirm: 'this IS the confirm dialog — the ask already happened',
            attrs: { 'data-act': 'yes' },
          })
        : mkIconBtn({ glyph: 'x', label: b.label, attrs: { 'data-act': 'no' } });
      // Initial focus lands on the primary — EXCEPT for a destructive verb,
      // where it lands on Cancel (never hand Enter the loaded gun).
      if (b.act === (spec.danger === true ? 'no' : 'yes')) {
        btn.setAttribute('data-autofocus', '');
      }
      if (b.act === 'yes') {
        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          setBtnState(btn, 'busy'); // disables + shows the primitive's spinner
          close(); // the dialog leaves NOW …
          spec.onConfirm(); // … and the work rides behind it
        });
      } else {
        btn.addEventListener('click', close);
      }
      actions.appendChild(btn);
    }
    body.appendChild(actions);
  }, { className: 'hc-confirm', onClose: spec.onClose }, vocab);
}

// ------------------------------------------------------------ inline tier

const ARM_MS = 4000;
/** The armed glyph, one for the whole product. */
const ARM_GLYPH = 'st-check';

interface Armed {
  btn: HTMLButtonElement;
  /** null when the element carries no sprite glyph — a TEXT affordance (a
   * destructive menu row) that opted into the same confirm interaction. */
  glyph: string | null;
  text: string | null;
  label: string;
  timer: number;
}

let armed: Armed | null = null;

function disarm(): void {
  if (!armed) return;
  const { btn, glyph, text, label, timer } = armed;
  window.clearTimeout(timer);
  armed = null;
  delete btn.dataset.btnArmed;
  if (glyph !== null) setBtnGlyph(btn, glyph);
  if (text !== null) btn.textContent = text;
  if (label) setBtnLabel(btn, label);
}

/** Exported for the suites and for a view that tears itself down mid-arm. */
export function disarmDestructive(): void {
  disarm();
}

/** True while `btn` is the armed one — the second click is the real click. */
export function isArmed(btn: Element | null): boolean {
  return !!armed && armed.btn === btn;
}

function arm(btn: HTMLButtonElement): void {
  disarm();
  const glyph = btn.querySelector('use')?.getAttribute('href')?.replace('#i-', '') ?? null;
  // A destructive TEXT affordance (a menu row) arms the same way: the confirm
  // interaction belongs to the destructive INTENT, not to the icon-button
  // skin, so a `data-btn="destructive"` menu item gets the identical two-step
  // rather than a fifth confirm mechanism of its own.
  const text = glyph === null ? btn.textContent : null;
  const label = btn.getAttribute('aria-label') ?? '';
  const question = btn.dataset.btnConfirm || `${label || text || 'This'}?`;
  armed = { btn, glyph, text, label, timer: window.setTimeout(disarm, ARM_MS) };
  btn.dataset.btnArmed = '1';
  if (glyph !== null) setBtnGlyph(btn, ARM_GLYPH);
  else btn.textContent = question;
  if (label) setBtnLabel(btn, question);
}

/** Anything that declared a question. Destructive buttons always have one;
 *  another intent has one only when its call site opted in. */
const CONFIRM_SEL = '[data-btn][data-btn-confirm]';

/**
 * Install the INLINE tier. Call once per document at boot (each host does it
 * in its own boot path); the returned function uninstalls. Capture phase on
 * the document means it runs before every delegated handler in every view, so
 * a destructive button gains its gate with no change at its own call site.
 */
export function installConfirmDestructive(doc: Document = document): () => void {
  const swallow = (e: Event): void => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(CONFIRM_SEL);
    if (!btn || btn.disabled) return;
    if (isArmed(btn)) {
      // The confirming click: let it through untouched, and drop the arm so a
      // re-render (or a second visit) starts cold.
      if (e.type === 'click') disarm();
      return;
    }
    // Anything else — including a click on a DIFFERENT destructive button
    // while one is armed — is the ARMING click and must not reach the action.
    e.preventDefault();
    e.stopPropagation();
    if ('stopImmediatePropagation' in e) (e as Event & { stopImmediatePropagation(): void }).stopImmediatePropagation();
    if (e.type === 'click') arm(btn);
  };
  const away = (e: Event): void => {
    if (!armed) return;
    const t = e.target as HTMLElement | null;
    if (t && t.closest(CONFIRM_SEL) === armed.btn) return;
    disarm();
  };
  const key = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') disarm();
  };
  const leave = (e: Event): void => {
    if (armed && (e.target as HTMLElement | null)?.closest(CONFIRM_SEL) === armed.btn) disarm();
  };
  // pointerdown/mousedown are swallowed too: a handful of call sites act on
  // press rather than click, and an arm that only guards `click` would leave
  // those firing on the first press.
  for (const type of ['click', 'pointerdown', 'mousedown']) doc.addEventListener(type, swallow, true);
  doc.addEventListener('pointerdown', away, true);
  doc.addEventListener('keydown', key, true);
  doc.addEventListener('focusout', leave, true);
  doc.addEventListener('pointerleave', leave, true);
  return () => {
    disarm();
    for (const type of ['click', 'pointerdown', 'mousedown']) doc.removeEventListener(type, swallow, true);
    doc.removeEventListener('pointerdown', away, true);
    doc.removeEventListener('keydown', key, true);
    doc.removeEventListener('focusout', leave, true);
    doc.removeEventListener('pointerleave', leave, true);
  };
}
